/**
 * Safely turn any backend error payload into a single human-readable string
 * that can be passed to sonner's `toast.error(...)`.
 *
 * FastAPI / Pydantic v2 responds to validation failures with
 *   { detail: [{ type, loc, msg, input, ctx, url }, ...] }
 * Passing that array straight into a toast (or any React child) crashes the
 * tree with "Objects are not valid as a React child". This helper flattens
 * every shape we may encounter into a string with a sensible fallback.
 */
export function extractErrorMessage(err, fallback = "Something went wrong.") {
  if (!err) return fallback;
  // axios error shape
  const data = err?.response?.data;
  const detail = data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const msgs = detail
      .map((d) => {
        if (typeof d === "string") return d;
        if (d && typeof d === "object") {
          const where = Array.isArray(d.loc) ? d.loc.filter((p) => p !== "body").join(".") : "";
          const msg = d.msg || d.message || "Invalid value";
          return where ? `${where}: ${msg}` : msg;
        }
        return null;
      })
      .filter(Boolean);
    if (msgs.length) return msgs.join("; ");
  }
  if (detail && typeof detail === "object") {
    return detail.msg || detail.message || JSON.stringify(detail);
  }
  if (typeof data === "string" && data.trim()) return data;
  if (err?.message && typeof err.message === "string") return err.message;
  return fallback;
}
